/**
 * Campaigns — clusters of correlated risk events that share an
 * endpoint + ASN + JA4 fingerprint. Cards are ranked by high-risk
 * volume; "Open in Investigations" copies the cluster's filter preset
 * onto the URL and switches to the Investigations tab.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { useUrlState } from "@/hooks/useUrlState";
import { pivotTo } from "@/lib/pivot";
import { Card, EmptyState, ErrorState, Skeleton } from "@/components/primitives";
import { CampaignCard } from "@/components/campaigns/CampaignCard";

export function Campaigns() {
  const { filters } = useFilters();
  const { tick } = useAutoRefresh();
  const [, patchUrl] = useUrlState();

  const q = useQuery({
    queryKey: ["campaigns", filters, tick],
    queryFn: ({ signal }) => api.campaigns(filters, signal),
  });

  function openInvestigations(preset: Record<string, string>) {
    // Clear any leftover narrow filters from prior pivots so the
    // Investigations table shows ONLY this cluster's events.
    pivotTo(patchUrl, preset, "investigations");
  }

  if (q.isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-56" />)}
      </div>
    );
  }
  if (q.error) return <Card><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card>;
  const campaigns = q.data?.data ?? [];
  if (!campaigns.length) {
    return <Card><EmptyState title="No active campaigns in this window." hint="Try widening the time range — campaigns need ≥ 100 events with p95 score ≥ 60." /></Card>;
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {campaigns.map((c) => (
        <CampaignCard key={c.id} campaign={c} onOpenInvestigations={openInvestigations} />
      ))}
    </div>
  );
}
